import { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation } from "react-router";
import { Link } from "react-router-dom";
import CasinoHeading from "../../casinoComponents/casinoHeading";
import BetTableJoker20 from "../../casinoComponents/betTableJoker20";
import BetTableTeenJoker from "../../casinoComponents/betTableTeenJoker";
import MyBet from "../../casinoComponents/myBet";
import CasinoTableLayout from "../../casinoComponents/casinoTableLayout";
import Joker20CardsOverlay from "../../casinoComponents/videoBox/overlays/Joker20CardsOverlay";
import Joker1CardsOverlay from "../../casinoComponents/videoBox/overlays/Joker1CardsOverlay";
import ViewMoreModal from "../../../components/ViewMoreModal";
import {
  getCasinoGameDetails,
  getMatchExposure,
  getCasinoOpenBets,
} from "../../../apiservices/CasionApi";
import { extractCasinoGame } from "../../../utils/casinoGame";
import { formatBetNation } from "../../../utils/casinoMyBets";
import TeenJokerVideo from "./TeenJokerVideo";
import TeenJokerRefResults from "./TeenJokerRefResults";
import "../../../styles/games/teenjoker.css";

/**
 * One page for the reference's three "Joker Teenpatti" tables (route slug = feed type):
 *   /admin/casino/teenjoker → Teenpatti Joker          (.casino-table.teenpatti-joker, joker20 layout)
 *   /admin/casino/joker1    → Unlimited Joker Oneday   (.casino-table.teenpatti-joker.joker1)
 *   /admin/casino/joker120  → Unlimited Joker 20-20    (.casino-table.teenpatti-joker.joker1)
 * The heading shows the last-results feed's res1.cname, like the reference.
 */
const GAMES = {
  teenjoker: { title: "Teenpatti Joker", tableClass: "teenpatti-joker", joker1: false },
  joker1: { title: "Unlimited Joker Oneday", tableClass: "teenpatti-joker joker1", joker1: true },
  joker120: { title: "Unlimited Joker 20-20", tableClass: "teenpatti-joker joker1", joker1: true },
};

export default function TeenJoker() {
  const { pathname } = useLocation();
  const gameName = extractCasinoGame(pathname) || "teenjoker";
  // Keyed by game so switching between the three routes starts from fresh state.
  return <TeenJokerPage key={gameName} gameName={gameName} />;
}

function TeenJokerPage({ gameName }) {
  const game = GAMES[gameName] || GAMES.teenjoker;
  // My Bets / View More names: Unlimited Joker bets name their joker ("Player A - Joker J").
  const jokerNation = (nat, bet) => formatBetNation(gameName, nat, bet);
  const iframeSrc = `https://stream-s-43.uhdmovies.online/casino-stream?id=${gameName}`;
  const [gameData, setGameData] = useState({});
  const [exposures, setExposures] = useState({});
  const [myBets, setMyBets] = useState([]);
  const [showViewMoreModal, setShowViewMoreModal] = useState(false);
  const [showResults, setShowResults] = useState(true);
  const [title, setTitle] = useState(game.title);

  useEffect(() => {
    const fetchGameData = async () => {
      try {
        const response = await getCasinoGameDetails(gameName);
        setGameData(response || {});
      } catch (err) {
        console.error("Error fetching game data:", err);
      }
    };

    const first = setTimeout(fetchGameData, 0);
    const interval = setInterval(fetchGameData, 1500);
    return () => {
      clearTimeout(first);
      clearInterval(interval);
    };
  }, [gameName]);

  const d = gameData?.data?.data;
  const matchId = d?.mid != null ? String(d.mid) : "";
  const sub = d?.sub;
  const tableData = useMemo(
    () => (Array.isArray(sub) ? [...sub].sort((a, b) => (a.sr || 0) - (b.sr || 0)) : []),
    [sub]
  );

  const fetchExposure = useCallback(async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = user?.user_id || user?.id || user?.staff_id || user?.owner_id;
      if (userId && matchId) {
        const response = await getMatchExposure(userId, matchId);
        if (response.success && response.data) {
          const exposureMap = {};
          response.data.forEach((item) => {
            const teamName = item.team_name || item.selection || item.player_name || "";
            const exposureAmount = parseFloat(item.exposure_amount) || 0;
            if (teamName && exposureAmount !== 0) {
              exposureMap[teamName] = exposureAmount;
              exposureMap[teamName.toLowerCase()] = exposureAmount;
              exposureMap[teamName.trim()] = exposureAmount;
            }
          });
          setExposures(exposureMap);
        }
      }
    } catch (error) {
      console.error("Error fetching exposure:", error);
    }
  }, [matchId]);

  const fetchMyBetsData = useCallback(async () => {
    if (!gameName) return;
    try {
      const response = await getCasinoOpenBets(gameName);
      if (response?.success && Array.isArray(response.data)) {
        const formattedBets = response.data.map((bet) => ({
          // My Bets "Gametype" column (casino_bets.game_name)
          description: bet.description ?? bet.game_name ?? bet.gameName,
          userName: bet.username || bet.userName || "-",
          matchedBet: bet.matchedBet || bet.nation || bet.selection || bet.player_name || "-",
          selection: bet.selection || bet.player_name || bet.matchedBet || "",
          player_name: bet.player_name || "",
          odds: bet.odds ?? bet.userrate ?? "0",
          stake: bet.stake ?? bet.amount ?? "0",
          placeDate: bet.placeDate ?? bet.created_at ?? "-",
          gametype: bet.gametype ?? bet.game_type ?? (bet.type === "lay" ? "Lay" : bet.type === "back" ? "Back" : "-"),
          type: bet.type || null,
          exposer: parseFloat(bet.exposer || bet.exposure_amount || "0") || 0,
          joker: bet.joker ?? null,
        }));
        setMyBets(formattedBets);
      }
    } catch (error) {
      console.error("Error fetching my bets (open-downline):", error);
    }
  }, [gameName]);

  // Admin places no bets: an odds click only refreshes exposure and the bet list.
  const handleBetClick = (value) => {
    if (!value) return;
    fetchExposure();
    fetchMyBetsData();
  };

  useEffect(() => {
    if (matchId) {
      const t = setTimeout(() => fetchExposure(), 2000);
      const exposureInterval = setInterval(fetchExposure, 2000);
      return () => {
        clearTimeout(t);
        clearInterval(exposureInterval);
      };
    }
  }, [matchId, fetchExposure]);

  useEffect(() => {
    const first = setTimeout(fetchMyBetsData, 0);
    const intervalId = setInterval(fetchMyBetsData, 3000);
    return () => {
      clearTimeout(first);
      clearInterval(intervalId);
    };
  }, [fetchMyBetsData]);

  const roundId = d?.mid || "";
  const remark = d?.remark ? String(d.remark) : "";
  const Overlay = game.joker1 ? Joker1CardsOverlay : Joker20CardsOverlay;
  const BetTable = game.joker1 ? BetTableTeenJoker : BetTableJoker20;

  return (
    <CasinoTableLayout
      tableClass={game.tableClass}
      rightSide={<MyBet bets={myBets} formatNation={jokerNation} onViewMore={() => setShowViewMoreModal(true)} />}
    >
      <>
        <CasinoHeading name={title} roundId={roundId} />{" "}
        <TeenJokerVideo
          gameType={gameName}
          iframeSrc={iframeSrc}
          cardsOverlay={d?.card ? <Overlay cards={d.card} /> : null}
          timerValue={d?.lt}
          showResults={showResults}
          onToggleResults={() => setShowResults((v) => !v)}
        />{" "}
        {d?.mid ? (
          <div className="casino-detail">
            <BetTable data={tableData} onBetClick={handleBetClick} exposures={exposures}>
              {remark ? (
                <div className="casino-remark mt-1 w-100">
                  <div className="remark-icon">
                    <img src={`${import.meta.env.BASE_URL}assets/img/icons/remark.png`} alt="" />
                  </div>{" "}
                  <marquee>{remark}</marquee>
                </div>
              ) : null}{" "}
              <div className="last-result-title">
                <h5>Last Result</h5>{" "}
                <Link to={`/admin/reports/casinoresult/${gameName}`} className="result-more">
                  View All
                </Link>
              </div>{" "}
              <div className={`casino-video-last-results${showResults ? "" : " hide-lr"}`}>
                <TeenJokerRefResults gameType={gameName} fallbackTitle={game.title} onTitle={setTitle} />
              </div>
            </BetTable>
          </div>
        ) : null}
      </>
      <ViewMoreModal
        show={showViewMoreModal}
        onHide={() => setShowViewMoreModal(false)}
        title="View More"
        sectionKey={gameName}
        formatNation={jokerNation}
      />
    </CasinoTableLayout>
  );
}
